import * as React from 'react';
import { type ReactNode } from 'react';

import { ThemeProvider } from '@emotion/react';
import styled from '@emotion/styled';
import { type WrappedFieldProps } from 'redux-form';
import { type EventHandler } from 'redux-form/lib/Field';

import Label, { type LabelProps } from '~/components/Label/Label';
import {
  checkedDisabledIcon,
  checkedHoverIcon,
  checkedIcon,
  partiallyCheckedDisabledIcon,
  partiallyCheckedHoverIcon,
  partiallyCheckedIcon,
  uncheckedDisabledIcon,
  uncheckedHoverIcon,
  uncheckedIcon,
} from '~/constants/icons';
import { colors } from '~/styles/constants';

type CheckboxLabelPosition = 'left' | 'right';
type CheckedTypes = 'partial' | boolean;

const CheckboxContainer = styled('div')`
  display: flex;
  align-items: center;
`;

interface BoxProps {
  checked?: CheckedTypes;
  disabled?: boolean;
  focused?: boolean;
  invalid?: boolean;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  onClick: (evt: any) => void;
}

interface BoxContainerProps {
  customStyles?: string;
  showBackgroundColor?: boolean;
}

const BoxContainer = styled('div')<BoxContainerProps>`
  display: flex;
  align-items: center;
  ${(props) => (props.customStyles ? `${props.customStyles};` : '')}
  ${(props) =>
    props.showBackgroundColor
      ? `background-color: ${colors.forest700}`
      : `background-color: ${colors.white}`};
  ${(props) => props.showBackgroundColor && `border: 2px solid ${colors.forest700}`};
`;

export const Box = styled('span')<BoxProps>`
  position: relative;
  height: 14px;
  width: 14px;
  line-height: 14px;
  font-size: 10px;
  text-align: center;
  display: flex;
  justify-content: center;
  align-items: center;
  cursor: ${(props) => (props.disabled ? `default` : `pointer`)};
  flex-shrink: 0;
`;

const SLIDER_SIZE = {
  lg: 20,
  md: 15,
  sm: 10,
};

const SLIDER_HORIZONTAL_SPACING = {
  lg: 5,
  md: 4,
  sm: 3,
};

type ToggleSize = 'lg' | 'md' | 'sm';

type ToggleTheme = {
  backgroundColor: string;
  height: number;
  sliderHorizontalSpacing: number;
  sliderSize: number;
  sliderVerticalSpacing: number;
  transform: string;
  width: number;
};

const disabledPrimaryColor = '#2bab76';
const disabledSecondaryColor = '#02727e';

const Toggle = styled('label')<{
  children?: ReactNode;
  disabled?: boolean;
  theme?: ToggleTheme;
}>`
  position: relative;
  display: inline-block;
  width: ${(props) => props.theme.width}px;
  height: ${(props) => props.theme.height}px;
  min-width: ${(props) => props.theme.width}px;
`;

const Slider = styled('span')<{ disabled?: boolean; focused?: boolean; theme?: ToggleTheme }>`
  position: absolute;
  border-radius: ${(props) => props.theme.height}px;
  cursor: ${(props) => (props.disabled ? `default` : `pointer`)};
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: ${(props) => props.theme.backgroundColor};
  transition: 0.4s;
  min-width: ${(props) => props.theme.sliderSize}px;
  z-index: 2;

  ::before {
    position: absolute;
    content: '';
    border-radius: 50%;
    height: ${(props) => props.theme.sliderSize}px;
    width: ${(props) => props.theme.sliderSize}px;
    bottom: ${(props) => props.theme.sliderVerticalSpacing}px;
    left: ${(props) => props.theme.sliderHorizontalSpacing}px;
    background-color: ${colors.white};
    transition: 0.4s;
    ${({ theme: { transform } }) => `transform: ${transform}`};
  }
`;

const InvisibleCheckbox = styled('input')`
  opacity: 0;
  height: 0;
  width: 0;
  margin: 0;
  position: absolute;
  z-index: 1;
  cursor: ${(props) => (props.disabled ? 'default' : 'pointer')};
`;

const ErrorMessage = styled('div')`
  margin-top: 5px;
  font-size: 12px;
  color: ${colors.dangerRed};
`;

const getIcon = (
  checked: CheckedTypes | undefined,
  disabled: boolean,
  focused: boolean,
  circle: boolean,
) => {
  switch (checked) {
    case true:
      if (disabled) return checkedDisabledIcon;
      if (focused) return checkedHoverIcon;
      if (circle) return null;
      return checkedIcon;
    case 'partial':
      if (disabled) return partiallyCheckedDisabledIcon;
      if (focused) return partiallyCheckedHoverIcon;
      return partiallyCheckedIcon;
    default:
      if (disabled) return uncheckedDisabledIcon;
      if (focused) return uncheckedHoverIcon;
      if (circle) return null;
      return uncheckedIcon;
  }
};

const guidGenerator = () => {
  const S4 = () => (((1 + Math.random()) * 0x10000) | 0).toString(16).substring(1);
  return `${S4() + S4()}'-'${S4()}-${S4()}-${S4()}-${S4() + S4() + S4()}`;
};

interface CheckboxProps {
  checked?: CheckedTypes;
  circle?: boolean;
  className?: string;
  customBoxStyles?: string;
  dataAnalytics?: string;
  dataE2E?: string;
  dataPendo?: string;
  dataTestid?: string;
  disabled?: boolean;
  errorMessage?: string;
  focused?: boolean;
  hideErrors?: boolean;
  id?: string;
  label?: JSX.Element | string;
  labelPosition?: CheckboxLabelPosition;
  labelProps?: LabelProps;
  onBlur?(value: CheckedTypes): void;
  onChange?(value: CheckedTypes): void;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  onFocus?: EventHandler<React.FocusEvent<any>>;
  tabIndex?: number;
  toggle?: boolean;
  toggleSize?: ToggleSize;
}

const Checkbox = (props: CheckboxProps) => {
  const onClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    const { disabled, onChange } = props;

    if (disabled) return;

    if (onChange) onChange(!props.checked);
  };

  const onInvisibleCheckboxBlur = () => {
    const { checked, onBlur } = props;

    if (onBlur) onBlur(checked!);
  };

  const renderErrorMessage = () => {
    const { errorMessage } = props;

    if (!errorMessage) return null;

    return <ErrorMessage>{errorMessage}</ErrorMessage>;
  };

  const getToggleThemeFromProps = (): ToggleTheme => {
    const toggleSize = props.toggleSize || 'lg';
    const sliderSize = SLIDER_SIZE[toggleSize];
    const horizontalSpacing = SLIDER_HORIZONTAL_SPACING[toggleSize];
    const width = (sliderSize + horizontalSpacing) * 2;
    const height = sliderSize + (horizontalSpacing - 1) * 2;
    const sliderTransition = width - sliderSize - horizontalSpacing * 2;

    const transform = props.checked ? `translateX(${sliderTransition}px)` : '';
    const backgroundColor = props.checked
      ? props.disabled
        ? disabledPrimaryColor
        : colors.primary
      : props.disabled
      ? disabledSecondaryColor
      : colors.secondary;

    return {
      backgroundColor,
      height,
      sliderHorizontalSpacing: horizontalSpacing,
      sliderSize,
      sliderVerticalSpacing: horizontalSpacing - 1,
      transform,
      width,
    };
  };

  let checkIcon: Maybe<JSX.Element> = null;
  let labelComponent: Maybe<JSX.Element> = null;
  const randomId = guidGenerator();
  const {
    checked,
    circle,
    className,
    customBoxStyles,
    dataAnalytics,
    dataE2E,
    dataPendo,
    dataTestid,
    disabled,
    errorMessage,
    focused,
    id,
    label,
    labelPosition,
    labelProps,
    tabIndex,
    toggle,
  } = props;

  const iconSrc = getIcon(checked, !!disabled, !!focused, !!circle);

  checkIcon = iconSrc ? (
    <img
      alt={checked ? 'Checked box' : 'Unchecked box'}
      data-analytics={dataAnalytics && `${dataAnalytics}Image`}
      src={iconSrc}
    />
  ) : (
    <span
      aria-label={checked ? 'Checked box' : 'Unchecked box'}
      data-analytics={dataAnalytics && `${dataAnalytics}Image`}
    />
  );

  if (label) {
    labelComponent = (
      <Label
        {...labelProps}
        data-e2e={dataE2E}
        data-pendo={dataPendo}
        dataAnalytics={dataAnalytics}
        disabled={disabled}
        htmlFor={id || randomId}
        labelPosition={labelPosition || 'right'}
      >
        {label}
      </Label>
    );
  }

  const invisibleCheckbox = (
    <InvisibleCheckbox
      data-analytics={dataAnalytics && `${dataAnalytics}Input`}
      disabled={disabled}
      id={id || randomId}
      onBlur={onInvisibleCheckboxBlur}
      onFocus={props.onFocus}
      tabIndex={tabIndex}
      type="checkbox"
    />
  );

  let checkBoxContent = (
    <BoxContainer
      customStyles={customBoxStyles}
      showBackgroundColor={props.circle && props.checked === true}
    >
      {toggle ? (
        <ThemeProvider theme={getToggleThemeFromProps()}>
          <Toggle>
            <Slider
              data-e2e={dataE2E}
              data-pendo={dataPendo}
              data-testid={dataTestid}
              disabled={disabled}
              focused={focused}
              onClick={onClick}
            />
            {invisibleCheckbox}
          </Toggle>
        </ThemeProvider>
      ) : (
        <Box
          checked={checked}
          data-e2e={dataE2E}
          data-pendo={dataPendo}
          data-testid={dataTestid}
          disabled={disabled}
          focused={focused}
          invalid={!!errorMessage}
          onClick={onClick}
        >
          {checkIcon}
          {invisibleCheckbox}
        </Box>
      )}
    </BoxContainer>
  );

  // Add Label based on specified position (default is right side)
  checkBoxContent =
    labelPosition === 'left' ? (
      <React.Fragment>
        {labelComponent}
        {checkBoxContent}
      </React.Fragment>
    ) : (
      <React.Fragment>
        {checkBoxContent}
        {labelComponent}
      </React.Fragment>
    );

  return (
    <React.Fragment>
      <CheckboxContainer className={className} id={id ? `${id}-container` : undefined}>
        {checkBoxContent}
      </CheckboxContainer>
      {renderErrorMessage()}
    </React.Fragment>
  );
};

export const CheckboxField = (props: CheckboxProps & WrappedFieldProps) => {
  const { hideErrors, input, meta, ...checkboxProps } = props;

  const errorMessage = !hideErrors && meta && meta.touched && meta.error ? meta.error : null;

  return (
    <Checkbox
      checked={input.value}
      data-e2e={checkboxProps.dataE2E}
      data-pendo={checkboxProps.dataPendo}
      data-testid={checkboxProps.dataTestid}
      dataAnalytics={checkboxProps.dataAnalytics}
      errorMessage={errorMessage}
      focused={meta.active}
      onBlur={input.onBlur}
      onChange={input.onChange}
      onFocus={input.onFocus}
      {...checkboxProps}
    />
  );
};

export default Checkbox;
